import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { StatCard } from "../components/shared/StatCard";
import { Spinner } from "../components/shared/Spinner";
import { reportsAPI } from "../services/api";
import { fmt, r1 } from "../components/shared/fmt";
import { useTitle } from "../hooks/useTitle";

export const InventoryPage: React.FC = () => {
	useTitle("Inventory");
	const { data, isLoading, refetch } = useQuery({
		queryKey: ["inventory"],
		queryFn: reportsAPI.inventory,
		retry: false,
	});
	const inv = data?.data || {};
	const items = inv.items || [];
	const sum = inv.summary || {};
	const alerts = inv.alerts || {};
	const [fSt, setFSt] = useState("all");
	const [search, setSearch] = useState("");

	const visible = items.filter(
		(i: any) =>
			(fSt === "all" || i.status === fSt) &&
			(!search || i.name.toLowerCase().includes(search.toLowerCase())),
	);

	if (isLoading) return <Spinner />;

	return (
		<div>
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "flex-start",
					marginBottom: 20,
					flexWrap: "wrap",
					gap: 12,
				}}>
				<div>
					<div className="page-title">Inventory Management</div>
					<div className="page-sub">
						Stock levels · Usage · Alerts · COGS tracking
					</div>
				</div>
				<div style={{ display: "flex", gap: 8 }}>
					<button className="btn-icon" onClick={() => refetch()}>
						🔄 Refresh
					</button>
					<button className="btn-primary">+ Record Delivery</button>
				</div>
			</div>

			<div className="stat-grid">
				<StatCard label="Total Items" value={sum.totalItems || 0} icon="📦" />
				<StatCard
					label="Stock Value"
					value={fmt(sum.totalStockValue || 0)}
					cls="green"
					icon="💰"
					accent="#06a384"
				/>
				<StatCard
					label="Critical"
					value={sum.criticalCount || 0}
					cls="red"
					icon="🔴"
					accent="#ef4444"
				/>
				<StatCard
					label="Low Stock"
					value={sum.lowStockCount || 0}
					cls="amber"
					icon="🟡"
					accent="#f59e0b"
				/>
				<StatCard
					label="Healthy"
					value={sum.healthyCount || 0}
					cls="green"
					icon="✅"
				/>
			</div>

			{/* Alerts */}
			{((alerts.critical || []).length > 0 ||
				(alerts.warning || []).length > 0) && (
				<div
					className="card"
					style={{ border: "1px solid rgba(239,68,68,.3)" }}>
					<div className="card-title" style={{ color: "var(--red)" }}>
						⚠️ Stock Alerts — Action Required
					</div>
					<div
						style={{
							display: "grid",
							gridTemplateColumns: "repeat(auto-fill,minmax(250px,1fr))",
							gap: 10,
						}}>
						{[...(alerts.critical || []), ...(alerts.warning || [])]
							.slice(0, 12)
							.map((item: any) => (
								<div
									key={item.id}
									style={{
										background: "rgba(239,68,68,.06)",
										border: "1px solid rgba(239,68,68,.3)",
										borderRadius: 8,
										padding: 12,
									}}>
									<div
										style={{
											display: "flex",
											alignItems: "center",
											gap: 8,
											marginBottom: 8,
										}}>
										<span style={{ fontSize: 20 }}>{item.emoji}</span>
										<div style={{ flex: 1 }}>
											<div style={{ fontSize: 12, fontWeight: 700 }}>
												{item.name}
											</div>
										</div>
										<span
											className={`badge badge-${item.min !== undefined ? "critical" : "low"}`}>
											{item.min !== undefined ? "🔴 Critical" : "🟡 Low"}
										</span>
									</div>
									<div style={{ fontSize: 11, color: "var(--muted)" }}>
										Stock: {item.stock} / 100
									</div>
									<button
										className="btn-primary"
										style={{
											width: "100%",
											marginTop: 8,
											fontSize: 11,
											padding: 7,
										}}>
										📦 Restock
									</button>
								</div>
							))}
					</div>
				</div>
			)}

			{/* Full table */}
			<div className="card">
				<div className="card-title">
					Full Inventory ({visible.length} items)
					<div className="card-actions">
						<input
							className="search-input"
							placeholder="🔍 Search..."
							value={search}
							onChange={(e) => setSearch(e.target.value)}
						/>
					</div>
				</div>
				<div className="filter-tabs">
					{[
						["all", "All"],
						["critical", "🔴 Critical"],
						["low", "🟡 Low"],
						["ok", "🟢 Healthy"],
					].map(([f, l]) => (
						<button
							key={f}
							className={`filter-tab ${fSt === f ? "active" : ""}`}
							onClick={() => setFSt(f)}>
							{l} (
							{f === "all"
								? items.length
								: items.filter((i: any) => i.status === f).length}
							)
						</button>
					))}
				</div>
				<div style={{ overflowX: "auto" }}>
					<table className="data-table">
						<thead>
							<tr>
								<th>Item</th>
								<th>Category</th>
								<th>Stock</th>
								<th>Level</th>
								<th>Status</th>
								<th>Days Left</th>
								<th>Unit Cost</th>
								<th>Stock Value</th>
								<th>Margin</th>
								<th>Actions</th>
							</tr>
						</thead>
						<tbody>
							{visible.map((item: any) => {
								const pct = Math.min(100, (item.stock / item.max) * 100);
								const bc =
									item.status === "critical"
										? "#ef4444"
										: item.status === "low"
											? "#f59e0b"
											: "#06a384";
								const dlc =
									item.daysLeft <= 3
										? "var(--red)"
										: item.daysLeft <= 7
											? "var(--amber)"
											: "var(--text2)";
								return (
									<tr key={item.id}>
										<td>
											<div
												style={{
													display: "flex",
													alignItems: "center",
													gap: 8,
												}}>
												<span style={{ fontSize: 20 }}>{item.emoji}</span>
												<span style={{ fontWeight: 600, fontSize: 12 }}>
													{item.name}
												</span>
											</div>
										</td>
										<td>
											<span
												className="tag"
												style={{
													background: `${item.catColor}18`,
													color: item.catColor,
												}}>
												{item.category}
											</span>
										</td>
										<td>
											<span
												style={{
													fontFamily: "Syne,sans-serif",
													fontSize: 14,
													fontWeight: 800,
													color: bc,
												}}>
												{item.stock}
											</span>
											<span style={{ fontSize: 10, color: "var(--muted)" }}>
												{" "}
												/ {item.max}
											</span>
										</td>
										<td style={{ minWidth: 100 }}>
											<div
												style={{
													position: "relative",
													height: 8,
													background: "var(--border2)",
													borderRadius: 99,
												}}>
												<div
													style={{
														height: "100%",
														width: `${pct}%`,
														background: bc,
														borderRadius: 99,
													}}
												/>
											</div>
											<div
												style={{
													fontSize: 9,
													color: "var(--muted)",
													marginTop: 2,
												}}>
												Min:{item.min} Reorder:{item.reorder}
											</div>
										</td>
										<td>
											<span className={`badge badge-${item.status}`}>
												{item.status === "critical"
													? "🔴 Critical"
													: item.status === "low"
														? "🟡 Low"
														: "🟢 OK"}
											</span>
										</td>
										<td style={{ fontWeight: 700, color: dlc, fontSize: 12 }}>
											{item.daysLeft < 999 ? `${item.daysLeft}d` : "—"}
										</td>
										<td style={{ color: "var(--muted)", fontSize: 12 }}>
											{fmt(item.unitCost)}
										</td>
										<td className="text-accent" style={{ fontWeight: 700 }}>
											{fmt(item.stockValue)}
										</td>
										<td>
											<span
												className={`tag ${item.margin > 60 ? "tag-green" : item.margin > 40 ? "tag-amber" : "tag-red"}`}>
												{r1(item.margin)}%
											</span>
										</td>
										<td>
											<button
												className="btn-secondary"
												style={{ fontSize: 10 }}>
												📦 Restock
											</button>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
				{visible.length === 0 && (
					<div className="empty-state">
						<div className="empty-icon">📦</div>
						<div>No items found</div>
					</div>
				)}
			</div>
		</div>
	);
};
